import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  DollarSign, 
  TrendingUp, 
  Clock, 
  Users, 
  Target,
  CheckCircle,
  AlertCircle,
  Calendar
} from "lucide-react";
import Navbar from "@/components/Navbar";

const GsaChallenge1CreatorRewards = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const challengeStats = {
    totalBounty: 7500,
    timeline: 45,
    submissions: 8,
    shortlisted: 3,
    daysRemaining: 32
  };

  const submissions = [
    {
      id: 1,
      title: "Integrated Digital Project Management System",
      contributor: "Sarah Chen",
      status: "Under Review",
      score: 85,
      submittedDate: "2024-12-15"
    },
    {
      id: 2,
      title: "Modular Construction Scheduling Framework", 
      contributor: "Marcus Johnson",
      status: "Shortlisted",
      score: 92,
      submittedDate: "2024-12-12"
    },
    {
      id: 3,
      title: "AI-Powered Bottleneck Detection Tool",
      contributor: "Elena Rodriguez",
      status: "Shortlisted", 
      score: 88,
      submittedDate: "2024-12-10"
    }
  ];

  const networkActivity = [
    { participant: "Julia De Rosi", action: "Created challenge", time: "13 days ago" },
    { participant: "Charles Edwards", action: "Composed requirements", time: "12 days ago" },
    { participant: "Heather Edwards", action: "Added evaluation criteria", time: "11 days ago" },
    { participant: "Giblet", action: "Confirmed technical feasibility", time: "11 days ago" },
    { participant: "Sarah Chen", action: "Submitted solution", time: "3 days ago" },
    { participant: "Marcus Johnson", action: "Submitted solution", time: "6 days ago" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
            alt="iSPAI Logo" 
            className="h-16 mx-auto mb-6 cursor-pointer"
            onClick={() => navigate("/gsa-channel")}
          />
          <h1 className="text-3xl font-bold mb-4">GSA Challenge #1 - Creator Dashboard</h1>
          <p className="text-lg text-gray-600">
            Federal Office Renovation Timeline Reduction Challenge
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Bounty</p>
                  <p className="text-2xl font-bold">{challengeStats.totalBounty}</p>
                  <p className="text-xs text-gray-500">tokens</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Submissions</p>
                  <p className="text-2xl font-bold">{challengeStats.submissions}</p>
                  <p className="text-xs text-gray-500">received</p>
                </div>
                <Target className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Shortlisted</p>
                  <p className="text-2xl font-bold">{challengeStats.shortlisted}</p>
                  <p className="text-xs text-gray-500">solutions</p>
                </div>
                <CheckCircle className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Days Left</p>
                  <p className="text-2xl font-bold">{challengeStats.daysRemaining}</p>
                  <p className="text-xs text-gray-500">remaining</p>
                </div>
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Progress</p>
                  <p className="text-2xl font-bold">30%</p>
                  <p className="text-xs text-gray-500">complete</p>
                </div>
                <TrendingUp className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold">Challenge Timeline</h3>
              <div className="text-right">
                <span className="text-sm font-medium text-orange-600">Submission Period</span>
                <br />
                <span className="text-sm text-gray-600">{challengeStats.daysRemaining} days remaining</span>
              </div>
            </div>
            <Progress value={30} className="h-3 bg-gray-200" />
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span className="text-green-600 font-medium">✓ Challenge Published</span>
              <span className="text-orange-600 font-medium">● Submission Period</span>
              <span>Evaluation</span>
              <span>Winner Selection</span>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="submissions">Submissions</TabsTrigger>
            <TabsTrigger value="network">Network Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Challenge Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Objective</h4>
                    <p className="text-gray-700">
                      Reduce the average design-to-occupancy timeline for federal office renovations by 20%
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Target Areas</h4>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">Project Management & Scheduling</Badge>
                      <Badge variant="outline">Construction & Renovation</Badge>
                      <Badge variant="outline">Design & Planning</Badge>
                      <Badge variant="outline">Regulatory Compliance</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="submissions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Solution Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {submissions.map((submission) => (
                    <div key={submission.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{submission.title}</h4>
                          <p className="text-sm text-gray-600">by {submission.contributor}</p>
                        </div>
                        <Badge 
                          variant={submission.status === "Shortlisted" ? "default" : "secondary"}
                        >
                          {submission.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Score: {submission.score}/100</span>
                        <span>Submitted: {submission.submittedDate}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="network" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Expert Network Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {networkActivity.map((activity, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-blue-500 text-white text-xs">
                          {activity.participant.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="text-sm">
                          <span className="font-medium">{activity.participant}</span> {activity.action}
                        </p>
                        <p className="text-xs text-gray-500">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4 mt-8">
          <Button 
            variant="outline" 
            onClick={() => navigate("/gsa-channel")}
            className="px-8 py-2"
          >
            Back to GSA Channel
          </Button>
          
          <Button 
            onClick={() => navigate("/gsa-call-for-solutions")}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2"
          >
            View Challenge Details
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GsaChallenge1CreatorRewards;